import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  interval,
  map,
  Observable,
  of,
  Subject,
  tap,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AbstractApiService } from '../abstract-api-service/abstract-api-service';
import {
  TransactionRequest,
  TransactionRequestInterface,
  TransactionRequestMapper,
} from 'src/app/model/TransactionRequest';
import { AppConstants } from 'src/app/constants/app-constants';
import { UserService } from '../user/user.service';
import { User } from 'src/app/model/User';
import { AuthService } from '../auth/auth.service';
import {
  AUTHORIZATION_HANDLE_ENDPOINT,
  REQUEST_CANCEL_ENDPOINT,
  REQUEST_CREATE_ENDPOINT,
  REQUESTS_BY_COMPANY_ENDPOINT,
} from 'src/app/constants/api-routes';
import { AuthorizationService } from '../authorization/authorization.service';
import { formatPlanAmount } from '../../shared/plan-pricing';

/** Api request/return types */

export interface HandleTransactionRequestResponse {
  preauth_id: string | null; // Null on decline
  session_id: string; // Transaction request id
  status: 'accepted' | 'declined';
  decision: 'accepted' | 'declined';
}

/**
 * Per-plan entry on create: plan id + buyer MDR split percentage (0–100).
 * Backend computes totals/fees from amount, plan MDR, and split.
 */
export interface CreateTransactionRequestPlanOffer {
  planId: number;
  /** Buyer share of MDR as 0–100 */
  splitPercentage: number;
}

/**
 * Payload for creating a transaction request (supplier).
 *
 * Multi-plan / pass-thru contract:
 * - `amount` is the supplier's desired net
 * - `plans` lists plan ids with per-plan split percentages
 * - Invoice file uploads are preserved via `fileIds`
 */
export interface CreateTransactionRequestData {
  amount: number;
  buyerId: string;
  memo: string;
  planId?: Number | null;
  plans?: CreateTransactionRequestPlanOffer[];
  poNumber: string;
  invoiceNumber: string;
  fileIds: string[];
}

export interface CancelTransactionRequestResponse {
  status: string;
}

export interface DecisionContext {
  userAgent?: string;
  platform?: string | null;
  decidedAt?: string; // ISO
  location?: { lat: number; lon: number } | null;
  /** Buyer-selected plan id when accepting a multi-plan (or single-plan) offer */
  planId?: number;
}

/**
 * TransactionRequestService handles data operations with Transaction Requests
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionRequestService extends AbstractApiService<TransactionRequest> {
  /** Observable that emits the latest list of transaction requests */
  protected _subject = new Subject<TransactionRequest[]>();
  public changes$ = this._subject.asObservable();

  /** A list of 'transaction requests' the user has access to */
  protected _collection?: TransactionRequest[];

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>('0:00');
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the service, used to initialize services
   *
   * @param _userService
   * @param _authService
   * @param _http
   */
  constructor(
    private _userService: UserService,
    private _authService: AuthService,
    private _authorizationService: AuthorizationService,
    protected _http: HttpClient,
  ) {
    super();

    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._collection = undefined;
        this._subject.next([]);
      }
    });

    // Continuously query the backend to receive new transaction requests
    this._startTimer(0);
    this.timer.subscribe((x: number) => {
      this._timers.splice(this._timers.indexOf(x));
      this._startTimer(x + 1);
      this._fetch();
    });
  }

  /**
   * Get a list of transaction requests
   *
   * @return A reference to the current collection
   */
  public getEntities(): TransactionRequest[] | undefined {
    return this._collection?.map((x) => x.copyOf());
  }

  /** Specialized request operations */

  /**
   * Submit a new transaction request. This should only be able to be done by suppliers
   *
   * @param request Data for the transaction request to create
   * @return Response from the backend
   */
  public createRequest$(
    request: CreateTransactionRequestData,
  ): Observable<TransactionRequest> {
    // Get company
    const company = this._userService.company;
    if (company == undefined) {
      throw new Error(
        'cannot accept transaction request without an active company',
      );
    }

    // Get company id
    const companyId = company.companyId;
    if (companyId == undefined) {
      throw new Error(
        'cannot create transaction request without a valid company ID',
      );
    }

    // Get company role
    const role = company?.companyType;
    if (role == undefined || role !== 'SUPPLIER') {
      throw new Error('only suppliers can create transaction requests');
    }

    // Validate request
    if (request.amount <= 0) {
      throw new Error('transaction request amount must be greater than 0');
    }
    if (request.buyerId == undefined || request.buyerId.trim() === '') {
      throw new Error('transaction request must have a buyer ID');
    }

    if (request.memo == undefined) {
      console.warn('transaction request does not have a memo');
    }
    if (request.poNumber == undefined || request.poNumber.trim() === '') {
      console.warn('transaction request does not have a PO number');
    }
    if ((!request.plans || request.plans.length === 0) && !request.planId) {
      throw new Error(
        'transaction request must include at least one plan offer',
      );
    }
    if (
      request.invoiceNumber == undefined ||
      request.invoiceNumber.trim() === ''
    ) {
      console.warn('transaction request does not have an invoice number');
    }
    if (request.fileIds == undefined) {
      console.warn('transaction request file IDs are undefined');
    }

    // Post to the backend (plan ids + split percentages)
    const url = REQUEST_CREATE_ENDPOINT(companyId);
    return this._http.post<TransactionRequestInterface>(url, request).pipe(
      // Map to class and save to collection
      map((intf: TransactionRequestInterface) => {
        const tr = TransactionRequestMapper.toModel(intf);
        this.refresh(tr, 'create');
        return tr;
      }),
    );
  }

  /**
   * Cancel a transaction request. This should only be able to be done by suppliers
   *
   * @param requestId ID of the transaction request to cancel
   */
  public cancelRequest$(
    requestId: string,
  ): Observable<CancelTransactionRequestResponse> {
    // Get company id
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) {
      throw new Error(
        'cannot accept transaction request without an active company',
      );
    }

    // Find the transaction request
    const tr = this._collection?.find((x) => x.id === requestId);
    if (tr == undefined) {
      throw new Error(`cannot find transaction request with id ${requestId}`);
    }

    // Make backend request
    const url = REQUEST_CANCEL_ENDPOINT(companyId, requestId);
    return this._http.put<CancelTransactionRequestResponse>(url, {}).pipe(
      tap(() => {
        tr.status = 'cancelled';
        this.refresh(tr, 'modify');
      }),
    );
  }

  /**
   * Handle a transaction request. This should only be able to be done by buyers
   *
   * @param requestId ID of the transaction request to accept/decline
   * @param decision 'accepted' or 'declined'
   * @param context optional client context for audit/debug (NOT trusted; server should still capture IP/UA)
   * @return TransactionRequest (updated locally)
   */
  public handleRequest(
    requestId: string,
    decision: 'accepted' | 'declined',
    context?: DecisionContext,
  ): Observable<TransactionRequest> {
    // Get company
    const company = this._userService.company;
    if (company == undefined) {
      throw new Error(
        'cannot accept transaction request without an active company',
      );
    }

    // Get company role
    const role = company?.companyType;
    if (role == undefined || role !== 'BORROWER') {
      throw new Error('only borrowers can accept transaction requests');
    }

    // Get company id
    const companyId = company.companyId;
    if (companyId == undefined) {
      throw new Error(
        'cannot accept transaction request without a valid company ID',
      );
    }

    // Find the transaction request
    const tr = this._collection?.find((x) => x.id === requestId);
    if (tr == undefined) {
      throw new Error(`cannot find transaction request with id ${requestId}`);
    }

    // Build payload (planId in context for multi-plan accept)
    const payload: {
      decision: 'accepted' | 'declined';
      planId?: number;
      context?: DecisionContext;
    } = { decision };
    if (context) {
      payload.context = context;
      if (context.planId != null) {
        payload.planId = context.planId;
      }
    }

    // Make backend request
    const url = AUTHORIZATION_HANDLE_ENDPOINT(companyId, requestId);
    return this._http.put<HandleTransactionRequestResponse>(url, payload).pipe(
      // Map to class and save to collection
      map((intf: HandleTransactionRequestResponse) => {
        tr.status = intf.decision;
        if (intf.decision === 'accepted' && context?.planId != null) {
          tr.authorized_plan_id = context.planId;
          const chosen = tr.plans.find((p) => p.plan_id === context.planId);
          if (chosen) {
            tr.authorized_total = chosen.total;
            tr.amount_label = formatPlanAmount(chosen.total);
            tr.plan_id = chosen.plan_id;
            tr.plan_desc = chosen.description || chosen.name;
            tr.plan_dur = chosen.duration;
          }
        }
        this.refresh(tr, 'modify');

        // If accepted, create new authorization
        if (intf.decision === 'accepted') {
          this._authorizationService.createAuthorizationFromRequest(tr, intf);
        }

        return tr;
      }),
    );
  }

  /** Helper functions */

  /**
   * Set a list of transaction requests to the current collection
   *
   * @param transactionRequests
   */
  private _setEntities(transactionRequests: TransactionRequest[]): void {
    this._collection = transactionRequests.map((x) => x.copyOf());
    this._subject.next(transactionRequests.map((x) => x.copyOf()));
  }

  /**
   * Get transaction requests from the backend. You should subscribe to the 'changes$'
   * field to receive updates
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user's active company
    const company = this._userService.company;
    if (company == undefined) return;

    // Get role and company id
    const role = company.companyType?.toLowerCase() as 'borrower' | 'supplier';
    const companyId = company.companyId;
    if (role == undefined || companyId == undefined) return;

    this._http
      .get<
        TransactionRequestInterface[]
      >(REQUESTS_BY_COMPANY_ENDPOINT(companyId, role))
      .subscribe({
        next: (
          transactionRequestInterfaces: TransactionRequestInterface[],
        ): void => {
          if (transactionRequestInterfaces == undefined) {
            console.error(
              `[ERR]: cannot cast backend output into transaction request list`,
            );
          }

          // Convert the interfaces to class values
          let transactionRequests: TransactionRequest[] = [];
          for (let transactionRequestInt of transactionRequestInterfaces) {
            transactionRequests.push(
              TransactionRequestMapper.toModel(transactionRequestInt),
            );
          }

          // Propagate changes if different
          this._setEntities(transactionRequests);
          console.log(`[INFO]: successfully queried transaction requests`);
        },
      });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await (function (delay: number) {
        return new Promise((r) => setTimeout(r, delay));
      })(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(
        `${minutes}:${('' + seconds).length === 1 ? '0' : ''}${seconds}`,
      );
    }
  }
}
